import React from 'react'
import { AlignLeft } from 'lucide-react'
import type { ComponentConfig } from '@puckeditor/core'
import { Section } from 'puck/components/Section'
import { WithLayout, withLayout } from 'puck/components/Layout'
import { ThemeCustomFields } from '../../theme-config'
import {
  FontWeight,
  ShadowProps,
  TypographyProps,
  fontWeightOptions,
  shadowStyleField,
  applyShadowStyle,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  applyTypography,
} from 'puck/fields/StyleFields'

export type TextProps = WithLayout<{
  align: 'left' | 'center' | 'right'
  text?: string
  color?: string
  padding?: string
  size?: string
  maxWidth?: string
  fontWeight?: FontWeight
  letterSpacing?: string
  lineHeight?: string
  shadow?: ShadowProps
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
}>

const TextInner: ComponentConfig<{
  props: TextProps
  fields: ThemeCustomFields
}> = {
  fields: {
    text: {
      label: 'Content',
      type: 'textarea',
      contentEditable: true,
    },
    color: {
      label: 'Font Color',
      type: 'colorPicker',
    },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    textTransform: textTransformField,
    size: {
      label: 'Font Size',
      type: 'text',
      placeholder: '16px',
    },
    fontWeight: {
      label: 'Font Weight',
      type: 'select',
      options: fontWeightOptions,
    },
    letterSpacing: {
      label: 'Letter Spacing',
      type: 'text',
      placeholder: '1px / 0.1em / 10%',
    },
    lineHeight: {
      label: 'Line Height',
      type: 'text',
      placeholder: '1.5 / 24px / 150%',
    },
    shadow: shadowStyleField,
    align: {
      label: 'Alignment',
      type: 'radio',
      labelIcon: <AlignLeft size={16} />,
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
    maxWidth: {
      label: 'Max Width (optional)',
      type: 'text',
      placeholder: '100px',
    },
  },
  defaultProps: {
    align: 'left',
    text: 'Text',
    size: '16px',
    color: '#000',
  },
  render: ({ align, text, size, color, maxWidth, fontWeight, letterSpacing, lineHeight, shadow, fontFamily, fontFamilyFallback, textTransform }) => {
    return (
      <Section maxWidth={maxWidth}>
        <span
          style={{
            display: 'flex',
            textAlign: align,
            width: '100%',
            fontSize: size,
            fontWeight: fontWeight ? Number(fontWeight) || 300 : 300,
            letterSpacing: letterSpacing || undefined,
            lineHeight: lineHeight || undefined,
            maxWidth,
            color,
            ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
            ...applyShadowStyle(shadow),
            justifyContent:
              align === 'center'
                ? 'center'
                : align === 'right'
                ? 'flex-end'
                : 'flex-start',
          }}
        >
          {text}
        </span>
      </Section>
    )
  },
}

export const Text = withLayout(TextInner)
